import {Checkbox, Icon, LoadingSpinner, Text} from '@/components-v2/atoms'
import {Pagination} from '@/components-v2/molecules/Pagination/Pagination'
import {Search} from '@/components-v2/molecules/Search/Search'
import {cn} from '@/helpers/shadcn/utils'
import {useState} from 'react'

/**
 * @param {Object} props
 * @param {string} [props.title='List']
 * @param {Array<Object>} [props.items=[]]
 * @param {Function} [props.onItemClick]
 * @param {Function} [props.onCheckboxChange]
 * @param {string} [props.searchPlaceholder='Search...']
 * @param {boolean} [props.showSearch=true]
 * @param {string} [props.className='']
 * @param {boolean} [props.separateItems=false]
 * @param {boolean} [props.selectable=true]
 * @param {boolean} [props.showSelectedCount=false]
 * @param {number} [props.selectedCount=0]
 * @param {boolean} [props.showPagination=false]
 * @param {number} [props.currentPage=0]
 * @param {number} [props.totalPages=0]
 * @param {Function} [props.onPageChange]
 * @param {boolean} [props.useExternalSearch=false]
 * @param {Function} [props.onSearchChange]
 * @param {boolean} [props.isLoading=false]
 */
export const SelectableList = ({
  title = 'List',
  items = [],
  onItemClick = () => {},
  onCheckboxChange = () => {},
  searchPlaceholder = 'Search...',
  showSearch = true,
  className = '',
  separateItems = false,
  selectable = true,
  showSelectedCount = false,
  selectedCount = 0,
  showPagination = false,
  currentPage = 0,
  totalPages = 0,
  onPageChange = () => {},
  useExternalSearch = false,
  onSearchChange = () => {},
  isLoading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const listItems = Array.isArray(items) ? items : []
  const filteredItems = useExternalSearch
    ? listItems
    : listItems.filter(item => item?.label?.toLowerCase().includes(searchTerm.toLowerCase()))

  const handleSearchChange = value => {
    setSearchTerm(value)
    if (useExternalSearch) onSearchChange(value)
  }

  const handleRowClick = item => {
    if (selectable) {
      onItemClick(item)
    } else {
      onCheckboxChange(item, !item?.checked)
    }
  }

  return (
    <div className={cn('flex flex-col p-4 text-[var(--color-text-primary)]', className)}>
      {title && (
        <Text headingLevel={4} className="mb-4">
          {title}
        </Text>
      )}

      {showSelectedCount && (
        <div className="mb-3 border-b border-[var(--color-border-default)] pb-3">
          <Text size="small" textColorClass="text-[var(--color-text-secondary)]">
            {selectedCount} item{selectedCount !== 1 ? '(s)' : ''} selected
          </Text>
        </div>
      )}

      {showSearch && (
        <Search
          className="mb-4 w-full"
          searchTerm={searchTerm}
          onUpdate={handleSearchChange}
          placeholderText={searchPlaceholder}
          isClearable
          onClear={() => setSearchTerm('')}
        />
      )}

      <div className="space-y-1">
        {isLoading ? (
          <div className="flex min-h-20 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex min-h-20 items-center justify-center">
            <Text size="small" textColorClass="text-[var(--color-text-secondary)]">
              {searchTerm ? 'No items found matching your search' : 'No items available'}
            </Text>
          </div>
        ) : (
          filteredItems.map((item, index) => (
            <div key={item?.id ?? index}>
              <div
                className={cn(
                  'flex items-center rounded-[var(--border-radius-small)] p-2 transition-colors',
                  item?.selected
                    ? 'bg-[var(--color-background-layer-02)]'
                    : 'hover:bg-[var(--color-background-layer-02)]'
                )}
              >
                <Checkbox
                  className="shrink-0"
                  checked={Boolean(item?.checked)}
                  onUpdate={checked => onCheckboxChange(item, checked)}
                  aria-label={`Select ${item?.label ?? 'item'}`}
                  size="small"
                />
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded p-2 text-left"
                  onClick={() => handleRowClick(item)}
                >
                  <span className="flex min-w-0 flex-1 items-center justify-between gap-3 pr-2">
                    <Text size="small" variant="bold" className="min-w-0 break-words">
                      {item?.label}
                    </Text>
                    {item?.subLabel && (
                      <Text size="xsmall" variant="bold" textColorClass="text-[var(--color-text-secondary)]">
                        {item.subLabel}
                      </Text>
                    )}
                  </span>
                  {selectable && <Icon icon="chevron_right" className="shrink-0" size="large" aria-hidden="true" />}
                </button>
              </div>
              {separateItems && index < filteredItems.length - 1 && (
                <div className="my-2 border-b border-[var(--color-border-default)]" />
              )}
            </div>
          ))
        )}
      </div>

      {showPagination && totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center pt-4">
          <Pagination
            pageCount={totalPages}
            currentPage={currentPage + 1}
            onUpdate={newPage => onPageChange(newPage - 1)}
          />
        </div>
      )}
    </div>
  )
}
